import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS, CONTACT } from "../../lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/10"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src={LOGO_URL}
              alt="Bittern Property Services"
              className="h-12 w-auto object-contain"
              data-testid="header-logo-img"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-serif-display text-xl tracking-wide">
                BITTERN
              </span>
              <span className="text-[10px] tracking-[0.28em] text-neutral-500 mt-1">
                PROPERTY SERVICES
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
            {NAV_LINKS.filter((l) => l.to !== "/story").map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `nav-link text-[11px] tracking-[0.1em] uppercase font-medium ${
                    isActive ? "text-black" : "text-neutral-600 hover:text-black"
                  }`
                }
              >
                {({ isActive }) => (
                  <span data-active={isActive}>{l.label}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-3">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="header-phone-link"
              className="hidden md:inline-flex items-center whitespace-nowrap text-[11px] tracking-[0.12em] uppercase font-medium text-neutral-800 hover:text-black transition-colors"
            >
              {CONTACT.phone}
            </a>
            <Link
              to="/contact"
              data-testid="header-quote-btn"
              className="hidden md:inline-flex items-center bg-black text-white hover:bg-neutral-800 transition-colors px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium"
            >
              Request a Quote
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 border border-black/10"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden border-t border-black/10 bg-white"
          data-testid="mobile-menu"
        >
          <div className="w-full max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-lg font-serif-display ${
                    isActive ? "text-black" : "text-neutral-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-black/10 flex flex-col gap-2">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="text-sm tracking-[0.16em] uppercase"
                data-testid="mobile-phone-link"
              >
                {CONTACT.phone}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="text-sm text-neutral-600"
                data-testid="mobile-email-link"
              >
                {CONTACT.email}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
